import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as needsApi from '@/api/needs';
import type { NeedFilters, NeedFormData, NeedStatus } from '@/types/need';

const NEEDS_KEY = ['needs'] as const;

function invalidateAllNeedQueries(queryClient: ReturnType<typeof useQueryClient>) {
  // Invalidate everything under the ['needs'] prefix — list, family, member, detail
  void queryClient.invalidateQueries({ queryKey: NEEDS_KEY });
}

export function useNeeds(filters?: NeedFilters) {
  return useQuery({
    queryKey: [...NEEDS_KEY, 'list', filters],
    queryFn: () => needsApi.getNeeds(filters),
  });
}

export function useNeed(id: string | undefined) {
  return useQuery({
    queryKey: [...NEEDS_KEY, 'detail', id],
    queryFn: () => needsApi.getNeed(id!),
    enabled: !!id,
  });
}

export function useFamilyNeeds(familyId: string | undefined) {
  return useQuery({
    queryKey: [...NEEDS_KEY, 'family', familyId],
    queryFn: () => needsApi.getFamilyNeeds(familyId!),
    enabled: !!familyId,
  });
}

export function useMemberNeeds(memberId: string | undefined) {
  return useQuery({
    queryKey: [...NEEDS_KEY, 'member', memberId],
    queryFn: () => needsApi.getMemberNeeds(memberId!),
    enabled: !!memberId,
  });
}

export function useCreateNeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: NeedFormData) => needsApi.createNeed(data),
    onSuccess: () => {
      invalidateAllNeedQueries(queryClient);
    },
  });
}

export function useUpdateNeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NeedFormData> }) =>
      needsApi.updateNeed(id, data),
    onSuccess: () => {
      invalidateAllNeedQueries(queryClient);
    },
  });
}

export function useDeleteNeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => needsApi.deleteNeed(id),
    onSuccess: () => {
      invalidateAllNeedQueries(queryClient);
    },
  });
}

export function useAddNeedUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ needId, data }: { needId: string; data: { content: string; status?: NeedStatus } }) =>
      needsApi.addNeedUpdate(needId, data),
    onSuccess: () => {
      invalidateAllNeedQueries(queryClient);
    },
  });
}
